/**
 * Challenge Store
 *
 * Zustand (vanilla) store for the challenge feature. The challenge module is a
 * single-source owned resource with a full CRUD + status lifecycle, so it
 * follows the plan/workout shape (the list lives in the store) rather than the
 * deliberately-lean custom-exercise store (whose leanness is driven by its
 * dual-source library list — a constraint challenges don't have).
 *
 * Scoped to what the challenge domain currently uses: the user's challenge list,
 * create/update, and delete with its undo. Status mutations stay in the
 * service until they are wired into the UI, at which point they should move
 * here too.
 *
 * Create/update re-throw after recording the error so the form page can keep
 * the user on the form (and surface the error in an Alert) instead of
 * navigating away. The list fetch records its error without re-throwing (the
 * list page renders it inline with a retry).
 */

import { createStore } from 'zustand/vanilla';

import { failureMessage } from './failure';
import { registerSessionScoped } from './session-scope';
import { challengeService } from '../services/challenge.service';

import type { FetchOptions } from './fetch-options';
import type { ID } from '../types';
import type { ChallengeSummary } from '../types/domain/challenge.types';
import type { ChallengeInput } from '../validation/challenge.schemas';

// =============================================================================
// STORE INTERFACES
// =============================================================================

export interface ChallengeStoreState {
  // Loading states
  isFetching: boolean;
  isCreating: boolean;
  isUpdating: boolean;

  // Error states
  fetchError: string | null;
  createError: string | null;
  updateError: string | null;

  // Data state
  challenges: ChallengeSummary[];
  /** Whose list is loaded — a restore re-fetches it. */
  username: string | null;
}

export interface ChallengeStoreActions {
  fetchChallenges: (username: string, options?: FetchOptions) => Promise<void>;
  createChallenge: (username: string, data: ChallengeInput) => Promise<void>;
  updateChallenge: (id: ID, data: ChallengeInput) => Promise<void>;
  deleteChallenge: (id: ID) => Promise<boolean>;
  restoreChallenge: (id: ID) => Promise<boolean>;

  clearErrors: () => void;
  reset: () => void;
}

export type ChallengeStore = ChallengeStoreState & ChallengeStoreActions;

// =============================================================================
// STORE IMPLEMENTATION
// =============================================================================

export const challengeStore = createStore<ChallengeStore>((set, get) => ({
  // =========================================================================
  // INITIAL STATE
  // =========================================================================

  isFetching: false,
  isCreating: false,
  isUpdating: false,
  fetchError: null,
  createError: null,
  updateError: null,
  challenges: [],
  username: null,

  // =========================================================================
  // CRUD ACTIONS
  // =========================================================================

  fetchChallenges: async (
    username: string,
    { fresh = false }: FetchOptions = {}
  ): Promise<void> => {
    if (fresh) challengeService.clearCache();
    set({ isFetching: true, fetchError: null, username });

    try {
      const challenges = await challengeService.getUserChallenges(username);
      set({ challenges });
    } catch (error) {
      const errorMessage = failureMessage(error, 'Failed to fetch challenges');
      set({ fetchError: errorMessage });
    } finally {
      set({ isFetching: false });
    }
  },

  createChallenge: async (
    username: string,
    data: ChallengeInput
  ): Promise<void> => {
    set({ isCreating: true, createError: null });

    try {
      await challengeService.createUserChallenge(username, data);
    } catch (error) {
      const errorMessage = failureMessage(error, 'Failed to create challenge');
      set({ createError: errorMessage });
      throw error;
    } finally {
      set({ isCreating: false });
    }
  },

  updateChallenge: async (id: ID, data: ChallengeInput): Promise<void> => {
    set({ isUpdating: true, updateError: null });

    try {
      await challengeService.updateChallenge(id, data);
    } catch (error) {
      const errorMessage = failureMessage(error, 'Failed to update challenge');
      set({ updateError: errorMessage });
      throw error;
    } finally {
      set({ isUpdating: false });
    }
  },

  deleteChallenge: async (id: ID): Promise<boolean> => {
    try {
      await challengeService.deleteChallenge(id);
      // Drop it locally rather than refetching — the undo toast needs the row
      // gone immediately, and a restore re-syncs from the server anyway.
      set({
        challenges: get().challenges.filter((challenge) => challenge.id !== id),
      });
      return true;
    } catch (error) {
      set({
        updateError: failureMessage(error, 'Failed to delete challenge'),
      });
      return false;
    }
  },

  restoreChallenge: async (id: ID): Promise<boolean> => {
    try {
      await challengeService.restoreChallenge(id);

      // Re-sync so the restored challenge reappears with its canonical
      // position and server-derived standing.
      const { username } = get();
      if (username) {
        const challenges = await challengeService.getUserChallenges(username);
        set({ challenges });
      }
      return true;
    } catch (error) {
      set({
        updateError: failureMessage(error, 'Failed to restore challenge'),
      });
      return false;
    }
  },

  // =========================================================================
  // STATE MANAGEMENT ACTIONS
  // =========================================================================

  clearErrors: (): void => {
    set({ fetchError: null, createError: null, updateError: null });
  },

  reset: (): void => {
    set({
      isFetching: false,
      isCreating: false,
      isUpdating: false,
      fetchError: null,
      createError: null,
      updateError: null,
      challenges: [],
      username: null,
    });
  },
}));

// =============================================================================
// SELECTORS
// =============================================================================

export const selectChallenges = (state: ChallengeStore): ChallengeSummary[] =>
  state.challenges;

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const selectChallengeLoading = (state: ChallengeStore) => ({
  isFetching: state.isFetching,
  isCreating: state.isCreating,
  isUpdating: state.isUpdating,
});

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const selectChallengeErrors = (state: ChallengeStore) => ({
  fetchError: state.fetchError,
  createError: state.createError,
  updateError: state.updateError,
});

// =============================================================================
// SESSION SCOPE
// =============================================================================

registerSessionScoped('challengeStore', () => {
  challengeStore.getState().reset();
});
